import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import AppAppBar from '../components/AppAppBar';
import AppTheme from '../AppTheme';
import Invest from '../components/Invest';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import { chartConfigs, getDefaultSeo } from '../utils/constants';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import HotTopics from '../components/HotTopics';

// 背景
const backgroundSx = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  zIndex: -1,
  backgroundColor: '#f6f4ea',
  backgroundImage: `
    radial-gradient(1000px 600px at 20% -10%, rgba(255,255,255,0.7), rgba(255,255,255,0) 60%),
    radial-gradient(900px 500px at 120% 0%, rgba(255,255,255,0.55), rgba(255,255,255,0) 65%),
    linear-gradient(180deg, rgba(0,0,0,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)
  `,
  backgroundSize: `
    auto,
    auto,
    100% 28px,
    28px 100%
  `,
};

export default function ChartPage(props) {
  // ✅ 取得網址列的 chartType 參數
  const { chartType } = useParams();
  const currentChart = chartType || "keyAssets"; // 預設圖表類型
  // ✅ 直接從 constants.js 讀取 SEO 配置，若無則使用預設
  const seo = chartConfigs[currentChart]?.seo || getDefaultSeo(currentChart);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const bitcoinAddr = "15vau9uJTQfouqDDCECFG4ktELg6thimB7";

  // 移动端自动显示弹窗（只显示一次）
  useEffect(() => {
    if (isMobile) {
      const hasShownDialog = localStorage.getItem('donationDialogShown');
      if (!hasShownDialog) {
        setDialogOpen(true);
        localStorage.setItem('donationDialogShown', 'true');
      }
    }
  }, [isMobile]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(bitcoinAddr);
      setSnackbarOpen(true);
      if (isMobile) {
        setDialogOpen(false); // 复制后关闭弹窗
      }
    } catch (err) {
      console.error("複製失敗", err);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />

      {/* ✅ 注入 SEO Meta 標籤與 Schema.org 數據集 */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dataset",
            "name": seo.title,
            "description": seo.description,
            "creator": { "@type": "Organization", "name": "經濟簡訊網" }
          })}
        </script>
      </Helmet>
      
      <AppAppBar />
      <Box sx={backgroundSx} />

      {/* 桌面端：左侧捐款广告 */}
      {!isMobile && (
        <Box sx={{
          position: "fixed",
          top: "80px",
          left: "20px",
          width: "280px",
          zIndex: 10,
        }}>
          <Paper elevation={3} sx={{ p: 3, bgcolor: "#fff8e1", borderRadius: 3, boxShadow: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "#8b4513" }}>
              支持本站運營
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ lineHeight: 1.8 }}>
              本網站未投放任何商業廣告。<br />
              如果你覺得這個網站有用，<br />
              不妨投幣支持一下吧～
            </Typography>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, color: "#444" }}>
              比特幣（BTC主網）地址：
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.9rem", flex: 1 }}
              >
                {bitcoinAddr}
              </Typography>
              <Button 
                size="small" 
                variant="contained" 
                color="primary" 
                onClick={handleCopy} 
                startIcon={<ContentCopyIcon />}
              >
                複製
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* 移动端：捐款弹窗 */}
      {isMobile && (
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog}
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              mx: 2
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: "#fff8e1", 
            fontWeight: "bold", 
            color: "#8b4513",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            支持本站運營
            <IconButton 
              onClick={handleCloseDialog} 
              size="small"
              sx={{ color: "#8b4513" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ bgcolor: "#fff8e1", pb: 2 }}>
            <Typography variant="body1" gutterBottom sx={{ lineHeight: 1.6, mt: 1 }}>
              本網站未投放任何商業廣告。如果你覺得這個網站有用，不妨投幣支持一下吧～
            </Typography>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, color: "#444" }}>
              比特幣（BTC主網）地址：
            </Typography>
            <Box sx={{ 
              bgcolor: "white", 
              p: 2, 
              borderRadius: 2, 
              border: "1px solid #ddd",
              mt: 1
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  wordBreak: "break-all", 
                  fontFamily: "monospace", 
                  fontSize: "0.85rem",
                  color: "#333",
                  textAlign: "center",
                  lineHeight: 1.4
                }}
              >
                {bitcoinAddr}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ bgcolor: "#fff8e1", px: 3, pb: 3 }}>
            <Button 
              variant="outlined" 
              onClick={handleCloseDialog}
              sx={{ mr: 1 }}
            >
              關閉
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCopy} 
              startIcon={<ContentCopyIcon />}
            >
              複製地址
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* 主內容 */}
      <Container
        maxWidth="lg"
        component="main"
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          my: isMobile ? 1 : 12,
          gap: 4,
          px: isMobile ? 1 : 3,
          pt: isMobile ? 7.5 : 0
        }}
      >
        <Box sx={{ bgcolor: "#f6f6f6", p: { xs: 1.5, md: 3 }, borderRadius: 2, boxShadow: 1 }}>
          
          {/* 🌟 浮水印外層容器：設定 relative 作為定位基準，避免影響外部排版 */}
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            
            {/* 1. 原本的中央大浮水印 (經濟簡訊網) */}
            <Box 
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-15deg)', 
                fontSize: { xs: '2.5rem', sm: '4rem', md: '6rem' }, 
                fontWeight: 900,
                color: 'rgba(150, 150, 150, 0.08)', 
                pointerEvents: 'none',  
                userSelect: 'none',     
                zIndex: 10,             
                whiteSpace: 'nowrap',   
                letterSpacing: { xs: '0.5rem', md: '1.5rem' }
              }}
            >
              經濟簡訊網
            </Box>

            {/* 2. ✨ 新增的左上角網址浮水印（推入圖表內部） */}
            <Box
              sx={{
                position: 'absolute',
                // 💡 關鍵：加大 top 和 left，跨越 Y 軸與圖表邊距
                // 這裡的數值 (60px, 80px) 請依據你圖表的 Y 軸數字寬度進行微調
                top: { xs: 150, md: 130 },   
                left: { xs: 50, md: 130 },  
                fontSize: { xs: '0.7rem', md: '0.9rem' }, 
                color: 'rgba(150, 150, 150, 0.5)',        
                pointerEvents: 'none',    
                userSelect: 'none',
                zIndex: 10,
                fontFamily: 'monospace',  
                fontWeight: 'bold',
              }}
            >
              economicmessage
            </Box>

            {/* ✅ 將網址取得的 chartType 傳給 Invest 組件 */}
            <Invest initialChartType={chartType} />

          </Box>
          
          {/* ✅ 隱藏殺手鐧：為 Google 爬蟲準備的結構化說明文字，不破壞 UI 但極度強化關鍵字密度 */}
          {currentChart !== "default" && (
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px dashed #ccc' }}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#555', mb: 1 }}>
                深度經濟指標解析
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.8 }}>
                {seo.spiderText}
              </Typography>
            </Box>
          )}
        </Box>
      </Container>

      {/* 複製提示 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Bitcoin 地址已複製"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </AppTheme>
  );
}